import { ChangeDetectionStrategy, Component, OnInit, OnDestroy, Input, signal } from '@angular/core';
import { Subject, Subscription, lastValueFrom, takeUntil } from 'rxjs';
import { DashboardModel, DashboardWorkspaceModel, UiComponentModel } from './models/dashboard.model';
import { ComponentLauncher } from './services/component-launcher';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { DashboardApiService } from './services/dashboard-api.service';
import { DashboardItemAddedMessage, WizardModeChangedMessage, WorkspaceCloned } from 'src/app/shared/models/mediator-messages.model';
import { UsersService } from '../../services/users-service';
import { ConfirmationService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { SessionStorageKeys, StoreService } from '../../services/store.service';
import { AppNotificationService } from 'src/app/shared/services/app-notification.service';
import { CurrentAppIdsDto } from '../../models/current-app-ids-dto';
import { User } from '../user-admin-page/user-model';
import { getRightSidePlacement } from './services/workspace-placement.helper';

@Component({
    selector: 'app-dashboard',
    template: `
      <p-confirmDialog
        header="Confirmation"
        icon="pi pi-exclamation-triangle"
      ></p-confirmDialog>

    <div class="dashboard">
      <app-design-presence-modal />
      <div class="workspace-area">
        @if (activeWorkspace(); as ws) {
          <app-workspace
            [workspace]="ws"
            [scrollToItemId]="scrollToItemId()"
            (uiComponentsRemoved)="handleUiComponentsRemoved($event)"
            (uiComponentXyUpdated)="handleUiComponentXyUpdated($event)"
            (workspaceRefreshed)="refreshWorkspace()"
          />
        }
      </div>
      <app-workspace-tabs
        [isLoading]="isLoading()"
        [workspaces]="dashboard?.workspaces"
        [selectedTabIndex]="activeWorkspace()?.index ?? 1"
        (workspaceInserted)="handleWorkspaceInsert($event)"
        (workspaceIdx)="handleWorkspaceIndex($event)"
        (workspaceDeleted)="handleWorkspaceDelete($event)"
        (workspaceRenamed)="handleWorkspaceRename($event)"
        (workspaceSelected)="handleWorkspaceSelect($event)"
        (workspacesCloned)="handleWorkspacesCloned()"
      />
    </div>
  `,
    styles: [`
    :host {
      display: block;
      width: 100%;
      flex: 1;
      min-height: 0;
    }
    .dashboard {
      height: 100%;
      width: 100%;
      display: flex;
      flex-direction: column;
    }
    .workspace-area {
      flex: 1;
      min-height: 0;
      position: relative;
      display: flex;
      flex-direction: column;
    }
  `],
    providers: [ComponentLauncher, DialogService, ConfirmationService],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DashboardContainerComponent implements OnInit, OnDestroy {

  @Input()
  public user!: User;

  private _designId!: string;
  private _destroy$ = new Subject<void>();
  private _subscriptions = new Subscription();
  private _previousComponent: string | null = null;
  private _wizardModeActive = false;
  private _pendingWorkspaceCloneDesignId: string | null = null;
  
  // Signals for reactive state (Angular 21 zoneless compatibility)
  public isLoading = signal<boolean>(false);
  public activeWorkspace = signal<DashboardWorkspaceModel | null>(null);
  public scrollToItemId = signal<string | null>(null);
  
  public dashboard!: DashboardModel;
  public newComponentId: string | null = null;

  constructor(
    private _componentLauncher: ComponentLauncher,
    private _dashboardApi: DashboardApiService,
    private _usersService: UsersService,
    private _mediator: MediatorService,
    private _storeService: StoreService,
    private _confirmationService: ConfirmationService,
    private _toaster: AppNotificationService
  ) {
    this._subscriptions.add(
      this._mediator.of(WorkspaceCloned)
        .pipe(takeUntil(this._destroy$))
        .subscribe((message: WorkspaceCloned) => {
          const targetDesignId = message?.designId ?? null;
          if (targetDesignId && targetDesignId !== this._designId) {
            this._pendingWorkspaceCloneDesignId = targetDesignId;
            return;
          }
          this._pendingWorkspaceCloneDesignId = null;
          this.handleDefaultWorkspaceApplied();
        })
    );
  }

  async ngOnInit() {
    this._subscriptions.add(
      this._storeService
        .watchSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS)
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: async (appIds) => {
            if (appIds?.designId && this._designId !== appIds.designId) {
              this._designId = appIds.designId;
              if (this._pendingWorkspaceCloneDesignId === appIds.designId) {
                this._pendingWorkspaceCloneDesignId = null;
                await this.handleDefaultWorkspaceApplied();
                return;
              }

              await this.fetchDashboardInitComponents();
            }
          },
          error: (error) => {
            console.error('watchSession error:', error);
          }
        })
    );

    this._subscriptions.add(
      this._mediator.of(DashboardItemAddedMessage)
        .pipe(takeUntil(this._destroy$))
        .subscribe(message => {
          this.handleUiComponentAdded(message.widgetIdentifier);
        })
    );

    this._subscriptions.add(
      this._mediator.of(WizardModeChangedMessage)
        .pipe(takeUntil(this._destroy$))
        .subscribe(message => {
          this._wizardModeActive = message.isEnabled;
        })
    );

  }

  async handleWorkspacesCloned() {
    await this.fetchDashboardInitComponents();
  }

  private async handleDefaultWorkspaceApplied(): Promise<void> {
    await this.fetchDashboardInitComponents();

    if (this.dashboard?.workspaces?.length > 0) {
      await this.handleWorkspaceSelect(this.dashboard.workspaces[0].index);
    }
  }

  async handleWorkspaceInsert(e: { idx: number }) {
    const inserted = await lastValueFrom(this._dashboardApi.insertWorkspace({ workspaceIndex: e.idx }));
    if (inserted) {
      await this.fetchDashboardInitComponents();
    }
  }

  async handleWorkspaceDelete(idx: number) {
    const deleted = await lastValueFrom(this._dashboardApi.deleteWorkspace(idx));
    if (deleted) {
      await this.fetchDashboardInitComponents();
    }
  }

  async handleWorkspaceRename(e: { idx: number, name: string }) {
    await lastValueFrom(this._dashboardApi.renameWorkspace(e.idx, e.name));
  }

  async handleWorkspaceSelect(idx: number) {
    const setWs = await lastValueFrom(this._usersService.updateCurrentAppIds({ dashboardId: this.dashboard.id, dashboardWorkspaceIndex: idx }));
    if (setWs) {
      await this.fetchDashboardInitComponents();
    }
  }

  async handleUiComponentsRemoved(uiComponents: UiComponentModel[]) {
    const currentWorkspace = this.activeWorkspace();
    if (!currentWorkspace) return;
    
    const updatedComponents = this._componentLauncher.removeComponents(uiComponents, currentWorkspace.uiComponents);
    // Update signal with new workspace reference for OnPush detection
    this.activeWorkspace.set({
      ...currentWorkspace,
      uiComponents: updatedComponents
    });
    await lastValueFrom(this._dashboardApi.updateUiComponents(updatedComponents));
  }

  async handleUiComponentAdded(uiComponentName: string) {
    if (this._wizardModeActive) {
      this._toaster.showInfo('Exit Wizard Mode to add components to the workspace.');
      return;
    }
    if (this._previousComponent === uiComponentName) {
      return;
    }

    this.setLoadingState(true);
    this._previousComponent = uiComponentName;
    setTimeout(() => this._previousComponent = null, 1000);

    try {
      const currentWorkspace = this.activeWorkspace();
      if (!currentWorkspace) {
        return;
      }

      const activeComponents = currentWorkspace.uiComponents;
      const allComponents = this._componentLauncher.getComponentRegistry();
      const uiComponentNameKey = allComponents.find((c: any) => c.key === uiComponentName)?.name;
      const componentIndex = activeComponents.findIndex((c: any) => c.name === uiComponentNameKey);

      this.scrollToItemId.set(null);
      if (componentIndex > -1) {
        this._confirmationService.confirm({
          message: `Component ${activeComponents[componentIndex].name} already exists in the workspace. Do you want to add another?`,
          header: 'Information',
          accept: async () => {
            await this.addComponent(uiComponentName);
          },
          reject: () => {
            this.scrollToItemId.set(activeComponents[componentIndex].id);
          }
        });
      } else {
        const sumOfAllActiveComponentRows = activeComponents.reduce((acc, c) => acc + c.rows, 0);
        if (sumOfAllActiveComponentRows > 90) {
          this._toaster.showInfo('You have reached the maximum workspace size. Please create a new workspace to add more components.');
          return;
        }

        await this.addComponent(uiComponentName);
      }
    } finally {
      this.setLoadingState(false);
    }
  }

  async refreshWorkspace(): Promise<void> {
    if (!this.activeWorkspace()) {
      return;
    }
    this.scrollToItemId.set(null);
    this.activeWorkspace.set(null);
    await this.fetchDashboardInitComponents();
  }

  async handleUiComponentXyUpdated(uiComponents: UiComponentModel[]) {
    if (!this.isLoading() && uiComponents?.length) {
      await lastValueFrom(this._dashboardApi.updateUiComponents(uiComponents));
    }
  }

  async handleWorkspaceIndex(e: { fromIdx: number, toIdx: number }) {
    const indexed = await lastValueFrom(this._dashboardApi.reIndexWorkspace(e.fromIdx, e.toIdx));
    if (indexed) {
      await lastValueFrom(this._usersService.updateCurrentAppIds({ dashboardId: this.dashboard.id, dashboardWorkspaceIndex: e.toIdx }));
      await this.fetchDashboardInitComponents();
    }
  }

  private async addComponent(uiComponentName: string): Promise<void> {
    const currentWorkspace = this.activeWorkspace();
    if (!currentWorkspace) return;
    
    const uiComponents = currentWorkspace.uiComponents;
    const uiComponent = this._componentLauncher.generateComponent(uiComponentName);
    if (!uiComponent) {
      this._toaster.showError(`Component '${uiComponentName}' is not registered and cannot be added.`);
      return;
    }

    const placement = getRightSidePlacement(uiComponents, uiComponent, 50);
    uiComponent.x = placement.x;
    uiComponent.y = placement.y;

    const uiComponentId = await lastValueFrom(this._dashboardApi.updateUiComponents([...uiComponents, uiComponent]));

    uiComponent.id = uiComponentId;
    // Update signal with new workspace reference - Angular automatically triggers change detection
    this.activeWorkspace.set({
      ...currentWorkspace,
      uiComponents: [...uiComponents, uiComponent]
    });
    this.scrollToItemId.set(uiComponentId);
  }

  private async fetchDashboardInitComponents() {
    this.setLoadingState(true);
    try {
      this.scrollToItemId.set(null);
      const previousWorkspace = this.activeWorkspace();
      this.dashboard = await lastValueFrom(this._dashboardApi.getDashboard());
      const newWorkspace = this.dashboard?.activeWorkspace ?? null;
      // Force workspace component remount when switching to a different workspace index to avoid
      // carrying stale GridStack state (e.g., previous tab auto-compact mode) into the next workspace.
      // Yield one UI cycle after clearing the workspace so Angular actually destroys the old grid instance.
      const switchingWorkspace = previousWorkspace !== null &&
        newWorkspace !== null &&
        previousWorkspace.index !== newWorkspace.index;
      if (switchingWorkspace) {
        this.scrollToItemId.set(null);
        this.activeWorkspace.set(null);
        await this.waitForUiCycle();
      }
      this.activeWorkspace.set(newWorkspace);
      this._componentLauncher.loadComponentInstances(newWorkspace?.uiComponents);
    } finally {
      this.setLoadingState(false);
    }
  }

  private async waitForUiCycle(): Promise<void> {
    await Promise.resolve();
  }

  private setLoadingState(isLoading: boolean): void {
    this.isLoading.set(isLoading);
  }

  ngOnDestroy() {
    this._subscriptions.unsubscribe();
    this._destroy$.next();
    this._destroy$.complete();
  }
}
